import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useValidateDemo } from '../hooks/useValidateDemo';
import { validateFormSchema, type ValidateFormValues } from '../schema/showcaseSchema';
import type { ApiError, FieldError, ValidateAnnotation } from '../types/showcase';

const ANNOTATIONS: { value: ValidateAnnotation; label: string; placeholder: string }[] = [
  { value: 'Email', label: '@Email', placeholder: 'user@example.com' },
  { value: 'Phone', label: '@Phone', placeholder: '010-1234-5678' },
  { value: 'BusinessNo', label: '@BusinessNo', placeholder: '123-45-67890' },
  { value: 'NoXss', label: '@NoXss', placeholder: '<script>alert(1)</script>' },
  { value: 'NoSqlInjection', label: '@NoSqlInjection', placeholder: "' OR 1=1 --" },
  { value: 'SafeFileName', label: '@SafeFileName', placeholder: 'report_2026.xlsx' },
  { value: 'KrResidentNo', label: '@KrResidentNo', placeholder: '900101-1234567' },
  { value: 'Password', label: '@Password', placeholder: 'Str0ng@Pass1' },
];

export function ValidatePanel() {
  const { t } = useTranslation();
  const { mutate, isPending, data, isError, isSuccess, reset } = useValidateDemo();
  const [demoError, setDemoError] = useState<ApiError | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    resetField,
    formState: { errors, isValid },
  } = useForm<ValidateFormValues>({
    resolver: zodResolver(validateFormSchema),
    defaultValues: { annotation: 'Email', value: '' },
  });

  const selectedAnnotation = watch('annotation');
  const currentPlaceholder =
    ANNOTATIONS.find((a) => a.value === selectedAnnotation)?.placeholder ?? '';

  function onSubmit(values: ValidateFormValues) {
    setDemoError(null);
    mutate(
      { annotation: values.annotation, value: values.value },
      {
        onError: (err: ApiError) => {
          // 422 SHOWCASE-422-001 is an INTENDED demo result — store as demoError
          setDemoError(err);
        },
      }
    );
  }

  const durationMs =
    data?.durationNanos != null ? (data.durationNanos / 1_000_000).toFixed(2) : null;

  const learningMessage = t(`showcase.validate.learning.${selectedAnnotation}`);

  return (
    <section aria-labelledby="validate-panel-title">
      <h2
        id="validate-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-4)',
          marginTop: 0,
        }}
      >
        {t('showcase.validate.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-6)' }}>
        {t('showcase.validate.subtitle')}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Annotation selector */}
        <fieldset
          style={{
            border: 'none',
            padding: 0,
            margin: '0 0 var(--spacing-4)',
          }}
        >
          <legend
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {t('showcase.validate.annotation')}
          </legend>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: 'var(--spacing-2)',
            }}
          >
            {ANNOTATIONS.map((ann) => (
              <label
                key={ann.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-2)',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  border: `1px solid ${
                    selectedAnnotation === ann.value
                      ? 'var(--color-brand-primary)'
                      : 'var(--color-surface-border)'
                  }`,
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  fontFamily: 'var(--font-family-mono)',
                  background:
                    selectedAnnotation === ann.value
                      ? 'var(--color-sidebar-active-bg)'
                      : 'transparent',
                  color:
                    selectedAnnotation === ann.value
                      ? 'var(--color-brand-primary)'
                      : 'var(--color-text-primary)',
                  transition: 'all 0.15s',
                }}
              >
                <input
                  type="radio"
                  value={ann.value}
                  {...register('annotation')}
                  style={{ accentColor: 'var(--color-brand-primary)' }}
                />
                {ann.label}
              </label>
            ))}
          </div>
        </fieldset>

        {/* Value input */}
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label
            htmlFor="validate-value"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {t('showcase.validate.value')}
          </label>
          <input
            id="validate-value"
            type="text"
            placeholder={currentPlaceholder}
            aria-label={t('showcase.validate.value')}
            aria-invalid={!!errors.value}
            aria-describedby={errors.value ? 'validate-value-error' : undefined}
            {...register('value')}
            disabled={isPending}
            style={{
              width: '100%',
              padding: 'var(--spacing-3)',
              border: `1px solid ${errors.value ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-family-mono)',
              background: isPending ? 'var(--color-surface-muted)' : 'var(--color-surface-background)',
              color: 'var(--color-text-primary)',
              boxSizing: 'border-box',
            }}
          />
          {errors.value && (
            <p
              id="validate-value-error"
              role="alert"
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-danger)',
                marginTop: 'var(--spacing-1)',
              }}
            >
              {errors.value.message}
            </p>
          )}
        </div>

        {/* Submit button */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-6)' }}>
          <button
            type="submit"
            disabled={isPending || !isValid}
            aria-busy={isPending}
            style={{
              padding: 'var(--spacing-3) var(--spacing-6)',
              background: isPending ? 'var(--color-text-muted)' : 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            {isPending && (
              <span
                aria-hidden="true"
                style={{
                  width: 14,
                  height: 14,
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'var(--color-text-inverse)',
                  borderRadius: '50%',
                  display: 'inline-block',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            )}
            {isPending ? t('showcase.validate.loading') : t('showcase.validate.execute')}
          </button>
          {(isSuccess || demoError) && (
            <button
              type="button"
              onClick={() => {
                reset();
                setDemoError(null);
                // B: reset form input value as well
                resetField('value');
              }}
              style={{
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'transparent',
                color: 'var(--color-text-secondary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('showcase.common.reset')}
            </button>
          )}
        </div>
      </form>

      {/* Result card */}
      {(isSuccess && data) && (
        <div
          data-testid="validate-result-card"
          role="region"
          aria-label={t('showcase.validate.resultLabel')}
          style={{
            border: `1px solid ${data.passed ? 'var(--color-success)' : 'var(--color-warning)'}`,
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            <span
              style={{
                fontSize: 'var(--font-size-lg)',
                color: data.passed ? 'var(--color-success)' : 'var(--color-warning)',
              }}
              aria-hidden="true"
            >
              {data.passed ? '✓' : '✗'}
            </span>
            <strong style={{ color: data.passed ? 'var(--color-success)' : 'var(--color-warning)' }}>
              {data.passed ? t('showcase.validate.result.passed') : t('showcase.validate.result.failed')}
            </strong>
            {durationMs && (
              <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)', marginLeft: 'auto' }}>
                {t('showcase.validate.result.duration')}: {durationMs}ms
              </span>
            )}
          </div>
          {!data.passed && data.fieldErrors.length > 0 && (
            <FieldErrorsTable fieldErrors={data.fieldErrors} />
          )}
        </div>
      )}

      {/* Demo error card (422 SHOWCASE-422-001 = intended failure) */}
      {demoError && (
        <div
          data-testid="validate-result-card"
          role="region"
          aria-label={t('showcase.validate.resultIntendedLabel')}
          style={{
            border: '1px solid var(--color-warning)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            <span style={{ color: 'var(--color-warning)', fontSize: 'var(--font-size-lg)' }} aria-hidden="true">✗</span>
            <strong style={{ color: 'var(--color-warning)' }}>{t('showcase.validate.result.failed')}</strong>
            <code
              style={{
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-xs)',
                background: 'var(--color-surface-elevated)',
                padding: '2px var(--spacing-2)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-warning)',
              }}
            >
              {demoError.statusCode} {demoError.code}
            </code>
          </div>

          {demoError.fieldErrors && demoError.fieldErrors.length > 0 && (
            <FieldErrorsTable fieldErrors={demoError.fieldErrors} />
          )}

          <p
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              marginTop: 'var(--spacing-3)',
              marginBottom: 0,
              fontStyle: 'italic',
            }}
          >
            {t('showcase.validate.intendedResult')}
          </p>
        </div>
      )}

      {/* Network error (unexpected) */}
      {isError && !demoError && (
        <div
          role="alert"
          style={{
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            marginBottom: 'var(--spacing-4)',
            color: 'var(--color-danger)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {t('showcase.validate.networkError')}
        </div>
      )}

      {/* B: Inline annotation description */}
      {(selectedAnnotation === 'NoSqlInjection' || selectedAnnotation === 'SafeFileName') && (
        <div
          style={{
            padding: 'var(--spacing-3)',
            background: 'var(--color-sidebar-active-bg)',
            border: '1px solid var(--color-brand-primary)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <strong style={{ color: 'var(--color-brand-primary)' }}>
            {selectedAnnotation === 'NoSqlInjection' ? '@NoSqlInjection' : '@SafeFileName'}
          </strong>{' '}
          {selectedAnnotation === 'NoSqlInjection'
            ? t('showcase.validate.annotationDesc.NoSqlInjection')
            : t('showcase.validate.annotationDesc.SafeFileName')}
        </div>
      )}

      {/* Learning message */}
      <div
        style={{
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <span aria-hidden="true" style={{ marginRight: 'var(--spacing-2)' }}>📚</span>
        <strong>{t('showcase.panel.learning')}</strong> {learningMessage}
      </div>
    </section>
  );
}

function FieldErrorsTable({ fieldErrors }: { fieldErrors: FieldError[] }) {
  const { t } = useTranslation();
  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        aria-label={t('showcase.validate.fieldErrorsTable')}
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 'var(--font-size-xs)',
          fontFamily: 'var(--font-family-mono)',
        }}
      >
        <thead>
          <tr>
            {[
              t('showcase.validate.result.field'),
              t('showcase.validate.result.code'),
              t('showcase.validate.result.message'),
              t('showcase.validate.result.rejectedValue'),
            ].map((h) => (
              <th
                key={h}
                scope="col"
                style={{
                  textAlign: 'left',
                  padding: 'var(--spacing-1) var(--spacing-2)',
                  borderBottom: '1px solid var(--color-surface-border)',
                  color: 'var(--color-text-muted)',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fieldErrors.map((fe, i) => (
            <tr key={i}>
              <td style={{ padding: 'var(--spacing-1) var(--spacing-2)', color: 'var(--color-text-secondary)' }}>{fe.field}</td>
              <td style={{ padding: 'var(--spacing-1) var(--spacing-2)', color: 'var(--color-warning)' }}>{fe.code}</td>
              <td style={{ padding: 'var(--spacing-1) var(--spacing-2)', color: 'var(--color-text-primary)' }}>{fe.message}</td>
              <td style={{ padding: 'var(--spacing-1) var(--spacing-2)', color: 'var(--color-text-muted)', wordBreak: 'break-all' }}>{fe.rejectedValue}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
